/**
 * Typed client for the auth JSON API.
 *
 * Two server contracts shape this module:
 *
 *  - **CSRF (PRD §11.3):** unsafe requests must carry `content-type:
 *    application/json` when they have a body, and a same-origin `Origin`. The
 *    browser sets Origin itself; we only have to be honest about the body. A
 *    bodyless DELETE deliberately sends no content type.
 *  - **Errors:** every non-2xx response uses the `{ error: { code, message,
 *    fields?, requestId? } }` envelope, which `ApiError` preserves so forms can
 *    render field-level messages.
 */
import type {
  AdminOrganization,
  ChangePasswordInput,
  ForgotPasswordInput,
  InvitationSummary,
  LoginInput,
  Me,
  MembershipSummary,
  OrganizationSummary,
  ResetPasswordInput,
  SessionSummary,
  SignupInput,
} from '@mindpeer/auth-shared';

/** The session cookie is HttpOnly, so every call must opt into credentials. */
const CREDENTIALS: RequestCredentials = 'include';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller simply is not signed in, which is not a real error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** A network failure or a response that was not the documented envelope. */
function unexpected(status: number): ApiError {
  return new ApiError(
    status,
    'unexpected_error',
    'Something went wrong. Please try again.',
  );
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const hasBody = body !== undefined;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: CREDENTIALS,
      headers: hasBody ? { 'content-type': 'application/json' } : {},
      body: hasBody ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text === '' ? undefined : JSON.parse(text);
  } catch {
    throw unexpected(response.status);
  }

  if (response.ok) return payload as T;

  const envelope = (payload as { error?: Record<string, unknown> } | undefined)?.error;
  if (!envelope || typeof envelope.code !== 'string' || typeof envelope.message !== 'string') {
    throw unexpected(response.status);
  }

  throw new ApiError(
    response.status,
    envelope.code,
    envelope.message,
    envelope.fields as Record<string, string[]> | undefined,
    envelope.requestId as string | undefined,
  );
}

const get = <T>(path: string) => request<T>('GET', path);
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
const del = <T>(path: string) => request<T>('DELETE', path);

/* ------------------------------------------------------------------ *
 * Response shapes not already described by the shared schemas
 * ------------------------------------------------------------------ */

export interface LoginResult {
  user: Me;
  requiresEmailVerification: boolean;
  awaitingOrganization: boolean;
}

export interface VerifyEmailResult {
  status: 'verified';
  emailVerified: boolean;
  organization: OrganizationSummary | null;
  awaitingOrganization: boolean;
  resolution: string;
}

export interface StatusMessage {
  status: string;
  message: string;
}

export const api = {
  signup: (input: SignupInput) => post<StatusMessage>('/api/signup', input),

  verifyEmail: (token: string) => post<VerifyEmailResult>('/api/verify-email', { token }),

  resendVerification: (email: string) =>
    post<StatusMessage>('/api/resend-verification', { email }),

  login: (input: LoginInput) => post<LoginResult>('/api/login', input),

  /** `global: true` ends every session, not just this browser's (PRD §11.4). */
  logout: (global = false) => post<{ status: string }>('/api/logout', { global }),

  forgotPassword: (input: ForgotPasswordInput) =>
    post<StatusMessage>('/api/forgot-password', input),

  resetPassword: (input: ResetPasswordInput) => post<StatusMessage>('/api/reset-password', input),

  changePassword: (input: ChangePasswordInput) =>
    post<StatusMessage>('/api/change-password', input),

  me: () => get<{ user: Me }>('/api/me'),

  memberships: () => get<{ memberships: MembershipSummary[] }>('/api/me/memberships'),

  setActiveOrganization: (organizationId: string) =>
    post<{ organization: OrganizationSummary }>('/api/me/active-organization', {
      organizationId,
    }),

  sessions: () => get<{ sessions: SessionSummary[] }>('/api/me/sessions'),

  revokeSession: (sessionId: string) =>
    del<{ status: string }>(`/api/me/sessions/${encodeURIComponent(sessionId)}`),

  /* ---- Administrative (platform admins only) ---- */

  adminOrganizations: () =>
    get<{ organizations: AdminOrganization[] }>('/api/admin/organizations'),

  createOrganization: (input: { name: string; slug?: string }) =>
    post<{ organization: AdminOrganization; entitledApplicationId: string }>(
      '/api/admin/organizations',
      input,
    ),

  organizationInvitations: (organizationId: string) =>
    get<{ invitations: InvitationSummary[] }>(
      `/api/admin/organizations/${encodeURIComponent(organizationId)}/invitations`,
    ),

  createInvitation: (organizationId: string, input: { email: string; roles: string[] }) =>
    post<{ invitation: InvitationSummary; organization: OrganizationSummary }>(
      `/api/admin/organizations/${encodeURIComponent(organizationId)}/invitations`,
      input,
    ),

  revokeInvitation: (organizationId: string, invitationId: string) =>
    del<{ status: string }>(
      `/api/admin/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}`,
    ),

  /* ---- Tenant self-service (an organization's own administrators) ---- */

  /**
   * No organization id in these paths: they act on the session's active
   * organization, which is the only one a tenant administrator has in view.
   */
  myOrganizationInvitations: () =>
    get<{ invitations: InvitationSummary[] }>('/api/organization/invitations'),

  createMyOrganizationInvitation: (input: { email: string; roles: string[] }) =>
    post<{ invitation: InvitationSummary }>('/api/organization/invitations', input),

  revokeMyOrganizationInvitation: (invitationId: string) =>
    del<{ status: string }>(
      `/api/organization/invitations/${encodeURIComponent(invitationId)}`,
    ),

  /** What the accept screen can show before anyone has signed in. */
  invitation: (token: string) =>
    get<{
      email: string;
      roles: string[];
      expiresAt: string;
      status: 'pending' | 'accepted' | 'revoked' | 'expired';
      organization: OrganizationSummary;
      continueUrl: string | null;
    }>(`/api/invitation?token=${encodeURIComponent(token)}`),

  acceptInvitation: (token: string) =>
    post<{ organization: OrganizationSummary; continueUrl: string | null }>(
      '/api/accept-invitation',
      { token },
    ),
};
